import React from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Spinner,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

const formatMemory = (value) => {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";

  return `${(value / 1024 ** 3).toFixed(2)} GB`;
};

const SystemCard = ({
  title,
  data,
  serverType,
  onRestart,
  isRestarting,
  fetchError,
  isFetching,
}) => {
  const isNode = serverType === 'node';

  // Direct data extraction
  const displayData = React.useMemo(() => {
    if (!data) return null;

    // Get the last app from serverStatus.apps array for Node servers
    const lastApp =
      isNode && data.serverStatus?.apps?.length > 0
        ? data.serverStatus.apps[data.serverStatus.apps.length - 1]
        : null;

    //STATUS
    const isRunning = isNode
      ? ["online", "running", "pm2_running"].includes(lastApp?.status)
      : data.serverStatus?.running;

    const status = isRunning ? "running" : "offline";
    const statusBg = isRunning ? "green.100" : "red.100";
    const statusColor = isRunning ? "green.700" : "red.700";

    //CPU USAGE
    const cpuUsage = isNode ? lastApp?.cpu || 0 : data.cpu?.usage_percent || 0;

    // RAM (Used / Total)
    const ramUsed = isNode
      ? formatMemory(data.memory?.used ?? data.memory?.active)
      : formatMemory(data.memory?.used);

    const ramTotal = isNode
      ? formatMemory(data.memory?.total ?? data.totalMemory)
      : formatMemory(data.memory?.total);

    // RAM AVAILABLE
    const ramAvailable = isNode
      ? formatMemory(data.memory?.available ?? data.freeMemory)
      : formatMemory(data.memory?.free);

    // UPTIME
    const seconds = isNode
      ? lastApp?.lastRestart
        ? Math.floor((Date.now() - new Date(lastApp.lastRestart)) / 1000)
        : 0
      : data.serverStatus?.startedAt
        ? Math.floor(
            (Date.now() - new Date(data.serverStatus.startedAt)) / 1000,
          )
        : 0;

    const uptime =
      seconds > 0
        ? (() => {
            const d = Math.floor(seconds / 86400);
            const h = Math.floor((seconds % 86400) / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            return (
              [d > 0 ? `${d}d` : "", h > 0 ? `${h}h` : "", m > 0 ? `${m}m` : ""]
                .filter(Boolean)
                .join(" ") || "0m"
            );
          })()
        : "0";

    return {
      status,
      statusBg,
      statusColor,
      cpuUsage,
      ramUsed,
      ramTotal,
      ramAvailable,
      uptime,
    };
  }, [data, isNode]);

  return (
    <Box bg="white" borderRadius="16px" w="100%" p={1} pl={2} mb={4}>
      <Text fontSize="16px" mb={3} fontWeight="700" fontFamily="DM Sans" color="gray.700">
        {title}
      </Text>

      <Box overflowX="auto" borderRadius="12px">
        <Table minWidth="1070px" sx={{ 'th, td': { paddingY: '6px', paddingX: '3px', fontSize: '13px', fontWeight: '500' } }}>
          <Thead bg="rgba(244, 247, 254, 1)">
            <Tr>
              <Th w="100px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">Restart</Flex>
              </Th>
              <Th w="100px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">Status</Flex>
              </Th>
              <Th w="100px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">Server</Flex>
              </Th>
              <Th w="100px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">CPU Usage %</Flex>
              </Th>
              <Th w="120px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">RAM (Used/Total)</Flex>
              </Th>
              <Th w="100px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">Available</Flex>
              </Th>
              <Th w="100px" height="40px" color="rgba(141, 151, 182, 1)" borderRight="1px solid rgba(216, 227, 252, 1)" fontFamily="DM Sans">
                <Flex justify="center" align="center">Uptime</Flex>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {isFetching ? (
              <Tr height="60px">
                <Th colSpan={7}>
                  <Flex justify="center" align="center" py={4}>
                    <Spinner size="sm" mr={2} color="blue.400" />
                  </Flex>
                </Th>
              </Tr>
            ) : isRestarting ? (
              <Tr height="60px">
                <Th colSpan={7}>
                  <Flex justify="center" align="center" py={4}>
                    <Spinner size="sm" mr={2} color="blue.400" />
                    <Text>Restarting server...</Text>
                  </Flex>
                </Th>
              </Tr>
            ) : fetchError ? (
              <Tr height="60px">
                <Th colSpan={7}>
                  <Flex justify="center" align="center" py={3}>
                    <Text color="red.500" fontSize="14px">
                      {serverType === 'node' ? 'Node' : 'DB'} Error: {fetchError.message}
                    </Text>
                  </Flex>
                </Th>
              </Tr>
            ) : !displayData ? (
              <Tr height="60px">
                <Th colSpan={7}>
                  <Flex justify="center" align="center" py={3}>
                    <Text>No data available</Text>
                  </Flex>
                </Th>
              </Tr>
            ) : (
              <Tr height="60px">
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">
                    {/* <Button size="xs" leftIcon={<FiRefreshCw />} onClick={onRestart} isLoading={isRestarting} isDisabled={isRestarting}> */}
                    <Button size="xs" leftIcon={<FiRefreshCw />} onClick={onRestart} isLoading={isRestarting} isDisabled={true}>
                      Restart
                    </Button>
                  </Flex>
                </Th>
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">
                    <Box bg={displayData.statusBg} color={displayData.statusColor} px={3} py={1} borderRadius="md" fontSize="12px" fontWeight="600">
                      {displayData.status}
                    </Box>
                  </Flex>
                </Th>
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">{isNode ? 'Node' : 'Database'}</Flex>
                </Th>
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">{displayData.cpuUsage}</Flex>
                </Th>
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">{displayData.ramUsed} / {displayData.ramTotal}</Flex>
                </Th>
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">{displayData.ramAvailable}</Flex>
                </Th>
                <Th borderRight="1px solid rgba(216, 227, 252, 1)">
                  <Flex justify="center" align="center">{displayData.uptime}</Flex>
                </Th>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default SystemCard;
